import { FC, ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import AutoGraphRounded from '@mui/icons-material/AutoGraphRounded'
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded'
import LocalOfferRounded from '@mui/icons-material/LocalOfferRounded'
import PsychologyRounded from '@mui/icons-material/PsychologyRounded'
import VideoLibraryRounded from '@mui/icons-material/VideoLibraryRounded'
import { Box, Skeleton, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'

import { optionalClickableProps } from '../../helpers/interactive'
import { maxInstallShareOf } from '../../helpers/relativeMetric'
import { formatDelta, NO_VALUE } from '../../helpers/scoreDisplay'
import type { PatternMixRow } from '../../helpers/showcase'
import { buildGameOverviewTldrText, buildGameScopeTitle, clampPct, deltaColor, getPatternMixRows, getTopRisingPatterns } from '../../helpers/showcase'
import { useGamePageNavigation } from '../../hooks/useGamePageNavigation'
import { useGameStoryPatterns } from '../../hooks/useGameStoryPatterns'
import { useMonthLabel } from '../../hooks/useMonthLabel'
import { useUAInsightsActions, useUAInsightsScope } from '../../hooks/useUAInsightsNavigation'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import type { GameCreativeDetailsData, StoryPattern } from '../../types'
import { CreativeAdCardGrid } from '../CreativeAdCardGrid/CreativeAdCardGrid'
import { KpiCard } from '../KpiCard/KpiCard'
import { MonthlyTldrCard } from '../MonthlyTldrCard/MonthlyTldrCard'
import { MotivationDiagnosis } from '../MotivationDiagnosis/MotivationDiagnosis'
import { MotivationDiagnosisLegend } from '../MotivationDiagnosis/MotivationDiagnosisLegend'
import { EmptyHint, ViewAllButton } from '../OverviewPrimitives/OverviewPrimitives'
import { PatternMixChart } from '../PatternMixChart/PatternMixChart'
import { SectionCard } from '../SectionCard/SectionCard'

/** Number of items each summary section previews before "View all" takes over. */
const TAG_PREVIEW = 6
const CREATIVE_PREVIEW = 5
/** Story Pattern Mix rows previewed (the largest game-vs-genre divergences). */
const MIX_PREVIEW = 5

interface GameOverviewTabProps {
  gameId: string | null
  /** Already-fetched creative details for the game (Motivations / Creative Ads / Tags). */
  data: GameCreativeDetailsData
}

/**
 * "Overview" tab for the UA Insights game page — the default landing tab. It
 * stitches together a one-glance summary of every other tab (Motivations,
 * Story Patterns, Creative Ads, Creative Tags), surfacing the headline numbers
 * and month-over-month trends, then routes the user into the relevant tab when
 * they want the full picture. Navigation is written to the same `tab` /
 * `motivation` / `pattern` URL params the other tabs read, so deep-linking and
 * browser back/forward stay consistent.
 */
export const GameOverviewTab: FC<GameOverviewTabProps> = ({ gameId, data }) => {
  const { t } = useTranslation('ua-insights')
  const { patternLabel, motivationLabel, tagLabel, dimensionLabel, genreLabel } = useUATaxonomy()
  const { openTagForGame } = useUAInsightsActions()
  const { month } = useUAInsightsScope()
  const formatMonth = useMonthLabel()
  // The Overview previews every tab, so it jumps to a tab (and clears any
  // drill-down) via the shared game-page navigation contract.
  const { goToTab, openMotivation, goToPattern: openPattern } = useGamePageNavigation()
  const { data: storyData, isLoading: storyLoading } = useGameStoryPatterns(gameId)

  // --- derived summaries ----------------------------------------------------
  const topMotivations = useMemo(
    () => [...data.motivationScores.creativeAds].sort((a, b) => b.avgIntensity - a.avgIntensity).filter((m) => m.avgIntensity > 0),
    [data.motivationScores.creativeAds]
  )

  const topTags = useMemo(() => [...data.tags].sort((a, b) => b.count - a.count), [data.tags])

  const topCreatives = useMemo(() => [...data.creativeAds].sort((a, b) => (b.installShare ?? 0) - (a.installShare ?? 0)), [data.creativeAds])
  const maxInstallShare = useMemo(() => maxInstallShareOf(topCreatives), [topCreatives])

  const totalAds = data.creativeAds.length

  // Only patterns the game actually uses (>=1 creative), mirroring the Story
  // Patterns tab so the Overview previews the exact same set.
  const gamePatterns = useMemo<StoryPattern[]>(() => (storyData ? storyData.patterns.filter((p) => p.creativesCount > 0) : []), [storyData])
  const topRising = useMemo(() => getTopRisingPatterns(gamePatterns), [gamePatterns])
  const topPatternByShare = useMemo(() => [...gamePatterns].sort((a, b) => b.sharePct - a.sharePct)[0], [gamePatterns])

  // Story Pattern Mix preview — mirrors the Story Patterns tab's headline card
  // (this game's pattern share vs. its genre). Built from the full pattern list
  // (so genre-only patterns the game skips can still surface as under-indexes)
  // and trimmed to the largest absolute divergences so the snippet leads with
  // the patterns where the game most over- or under-indexes its genre.
  const benchmarkLabel = storyData?.benchmarkGenre ? genreLabel(storyData.benchmarkGenre) : ''
  // Lead with the largest divergences (top by |gap|), then re-sort signed so the
  // diverging chart reads cleanly (over-indexes above the baseline, under below).
  const patternMixPreview = useMemo<PatternMixRow[]>(
    () =>
      storyData
        ? [...getPatternMixRows(storyData.patterns, patternLabel)]
            .sort((a, b) => Math.abs(b.gap) - Math.abs(a.gap))
            .slice(0, MIX_PREVIEW)
            .sort((a, b) => b.gap - a.gap)
        : [],
    [storyData, patternLabel]
  )

  const diagnosis = data.motivationDiagnosis
  const diagnosisAvailable = !!data.motivationScores.gameDesign?.length

  // Monthly Insight — a deterministic, templated narrative built from the
  // Overview's own stats (mirrors the dashboard's Monthly Insight card).
  const monthLabel = formatMonth(month)
  const tldrTitle = buildGameScopeTitle(data.game.name, monthLabel)
  const topBlindSpot = diagnosisAvailable ? diagnosis.blindSpots[0]?.motivation : undefined
  const tldrText = buildGameOverviewTldrText(t, {
    gameName: data.game.name,
    topPattern: topPatternByShare ? { theme: patternLabel(topPatternByShare.pattern), pct: Number(topPatternByShare.sharePct.toFixed(1)) } : undefined,
    topMotivation: topMotivations[0] ? { name: motivationLabel(topMotivations[0].motivation), intensity: topMotivations[0].avgIntensity } : undefined,
    risingPatterns: topRising.map((p) => ({ theme: patternLabel(p.pattern), delta: formatDelta(p.deltaPp) })),
    topBlindSpot: topBlindSpot ? motivationLabel(topBlindSpot) : undefined,
  })

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* KPI strip — the at-a-glance headline numbers, each routing onward. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
        <KpiCard
          icon={<VideoLibraryRounded />}
          label={t('overview_kpi_creatives')}
          primary={totalAds.toLocaleString()}
          primaryFontSize="2.25rem"
          color="primary"
          onClick={() => goToTab('creative-ads')}
        />
        <KpiCard
          icon={<PsychologyRounded />}
          label={t('overview_kpi_top_motivation')}
          primary={topMotivations[0] ? motivationLabel(topMotivations[0].motivation) : NO_VALUE}
          secondary={
            topMotivations[0] ? (
              <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'text.secondary' }}>
                {t('overview_intensity_value', { value: topMotivations[0].avgIntensity.toFixed(1) })}
              </Typography>
            ) : undefined
          }
          color="secondary"
          onClick={topMotivations[0] ? () => openMotivation(topMotivations[0].motivation.id) : undefined}
        />
        <KpiCard
          icon={<AutoGraphRounded />}
          label={t('overview_kpi_top_pattern')}
          primary={topPatternByShare ? patternLabel(topPatternByShare.pattern) : NO_VALUE}
          secondary={
            topPatternByShare ? (
              <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 'bold', color: deltaColor(topPatternByShare.deltaPp) }}>
                {formatDelta(topPatternByShare.deltaPp)}
              </Typography>
            ) : undefined
          }
          loading={storyLoading}
          color="info"
          onClick={topPatternByShare ? () => openPattern(topPatternByShare.pattern.id) : undefined}
        />
        <KpiCard
          icon={<LocalOfferRounded />}
          label={t('overview_kpi_top_tag')}
          primary={topTags[0] ? tagLabel(topTags[0].tag) : NO_VALUE}
          secondary={
            topTags[0] ? (
              <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'text.secondary' }}>
                {t('ad_count', { count: topTags[0].count })}
              </Typography>
            ) : undefined
          }
          color="warning"
          onClick={topTags[0] ? () => goToTab('creative-tags') : undefined}
        />
      </Box>

      {/* Monthly Insight — narrative summary generated from the stats above. */}
      <MonthlyTldrCard title={tldrTitle} text={tldrText} />

      {/* Motivation Blind Spots — the Overview surfaces the Motivations tab's
          "Where are the Blind Spots?" diagnosis verbatim (summary card, gap
          legend and the per-motivation blind-spot cards), so the snippet reads
          identically and routes onward into the full Motivations tab. */}
      <SectionCard
        title={t('overview_blind_spots_title')}
        headerCenter={diagnosisAvailable ? <MotivationDiagnosisLegend gameIcon={data.game.icon} /> : undefined}
        headerAction={<ViewAllButton onClick={() => goToTab('motivations')} label={t('overview_open_section', { section: t('tab_motivations') })} />}
      >
        <MotivationDiagnosis diagnosis={diagnosis} available={diagnosisAvailable} gameName={data.game.name} gameIcon={data.game.icon} hideSummary hideLegend />
      </SectionCard>

      {/* Story Patterns — the strongest trend signal (carries monthly deltas). */}
      <SectionCard
        title={t('overview_pattern_mix_heading', { benchmark: benchmarkLabel })}
        headerAction={<ViewAllButton onClick={() => goToTab('story-patterns')} label={t('overview_open_section', { section: t('tab_story_patterns') })} />}
      >
        {storyLoading ? (
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} variant="rounded" height={96} sx={{ flex: 1, borderRadius: 1 }} />
            ))}
          </Box>
        ) : patternMixPreview.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Story Pattern Mix — the headline of the Story Patterns tab: how
                this game's pattern share diverges from its genre. Previews the
                largest over/under-indexes as compact diverging bars. */}
            <Box>
              <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', lineHeight: 1.5, mb: 1.25 }}>
                {t('pattern_mix_hint', { benchmark: benchmarkLabel })}
              </Typography>
              <PatternMixChart
                rows={patternMixPreview}
                gameName={data.game.name}
                benchmarkLabel={benchmarkLabel}
                onSelectPattern={openPattern}
                variant="compact"
              />
            </Box>
          </Box>
        ) : (
          <EmptyHint text={t('game_story_patterns_empty')} />
        )}
      </SectionCard>

      {/* Creatives — top creatives by share-of-voice (reuses the card grid). */}
      <SectionCard
        title={t('overview_top_creatives')}
        headerAction={<ViewAllButton onClick={() => goToTab('creative-ads')} label={t('overview_open_section', { section: t('tab_creative_ads') })} />}
        disableBodyPadding
      >
        {topCreatives.length > 0 ? (
          <CreativeAdCardGrid rows={topCreatives.slice(0, CREATIVE_PREVIEW)} maxInstallShare={maxInstallShare} />
        ) : (
          <Box sx={{ p: 2 }}>
            <EmptyHint text={t('no_ads')} />
          </Box>
        )}
      </SectionCard>

      {/* Creative Tags — most prevalent tags with distribution bars. */}
      <SectionCard
        title={t('overview_top_creative_tags')}
        headerAction={<ViewAllButton onClick={() => goToTab('creative-tags')} label={t('overview_open_section', { section: t('tab_creative_tags') })} />}
      >
        {topTags.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <SummaryHeaderRow label={t('overview_tags_header_tag')} trailing={t('overview_tags_header_share')} />
            {topTags.slice(0, TAG_PREVIEW).map((tag) => {
              const distribution = totalAds > 0 ? (tag.count / totalAds) * 100 : 0
              return (
                <SummaryRow
                  key={tag.tag.id}
                  label={tagLabel(tag.tag)}
                  caption={dimensionLabel(tag.tag.dimensionId) || undefined}
                  barPct={distribution}
                  barColor="primary.main"
                  trailing={
                    <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 700 }}>
                      {`${distribution.toFixed(0)}%`}
                    </Typography>
                  }
                  onClick={() => gameId != null && openTagForGame(tag.tag.id, gameId)}
                />
              )
            })}
          </Box>
        ) : (
          <EmptyHint text={t('no_tags')} />
        )}
      </SectionCard>
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Local presentational helpers
// ---------------------------------------------------------------------------

interface SummaryRowProps {
  label: string
  caption?: string
  barPct: number
  barColor: string
  trailing: ReactNode
  onClick?: () => void
}

interface SummaryHeaderRowProps {
  label: string
  trailing: string
}

/**
 * Column-header row for the `SummaryRow` lists (e.g. Creative Tags): a left
 * label aligned over the row labels and a right-aligned header spanning the
 * bar + value columns, so the distribution numbers read with context.
 */
const SummaryHeaderRow: FC<SummaryHeaderRowProps> = ({ label, trailing }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1, mx: -1 }}>
    <Box sx={{ width: { xs: 120, sm: 170 }, flexShrink: 0, minWidth: 0 }}>
      <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>{label}</Typography>
    </Box>
    <Box sx={{ flex: 1, minWidth: 40 }}>
      <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
        {trailing}
      </Typography>
    </Box>
    <Box sx={{ width: 58, flexShrink: 0 }} />
  </Box>
)

/**
 * Clickable labelled progress row used by the tag preview. Mirrors the
 * Motivation Blind Spots card hover language: on hover the row tints + indents
 * in the bar's accent, the title shifts to the accent, a chevron reveals and
 * slides, and the distribution bar "comes alive" — the track swells, the fill
 * brightens and the trailing value pops — all on the same spring easing.
 */
const SummaryRow: FC<SummaryRowProps> = ({ label, caption, barPct, barColor, trailing, onClick }) => (
  <Box
    {...optionalClickableProps(onClick)}
    sx={(theme) => ({
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      px: 1,
      py: 0.625,
      mx: -1,
      borderRadius: 1,
      cursor: onClick ? 'pointer' : 'default',
      // A slim full-width row reads as "wobbly" under the Blind Spots cards'
      // overshoot spring, so keep this section on a smooth ease: tint, brighten
      // and a gentle bar swell / value pop with no bounce or sideways shift.
      // The "interest" comes from a light sweep that glides across the fill on
      // hover (a calm cousin of the Blind Spots stripe flow), plus a soft glow.
      '@keyframes summaryRowShimmer': {
        '0%': { transform: 'translateX(-160%)' },
        '100%': { transform: 'translateX(320%)' },
      },
      transition: 'background-color 0.18s ease',
      '& .summary-row-track': { transformOrigin: 'center', transition: 'transform 0.2s ease' },
      '& .summary-row-fill': { transition: 'filter 0.2s ease, box-shadow 0.2s ease' },
      '& .summary-row-value': { transformOrigin: 'right center', transition: 'transform 0.2s ease' },
      '& .summary-row-title': { transition: 'color 0.18s ease' },
      '& .summary-row-chevron': { transition: 'opacity 0.18s ease, transform 0.18s ease' },
      '&:hover': onClick
        ? {
            bgcolor: alpha(theme.palette.primary.main, 0.06),
            '& .summary-row-title': { color: 'primary.main' },
            '& .summary-row-track': { transform: 'scaleY(1.25)' },
            '& .summary-row-fill': {
              filter: 'saturate(1.12) brightness(1.05)',
              boxShadow: `0 0 8px ${alpha(theme.palette.primary.main, 0.5)}`,
            },
            '& .summary-row-value': { transform: 'scale(1.08)' },
            '& .summary-row-chevron': { opacity: 1, transform: 'translateX(2px)' },
            '@media (prefers-reduced-motion: no-preference)': {
              '& .summary-row-sheen': { animation: 'summaryRowShimmer 1.15s ease-in-out infinite' },
            },
          }
        : undefined,
      '&:focus-visible': onClick ? { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 } : undefined,
    })}
  >
    <Box sx={{ width: { xs: 120, sm: 170 }, flexShrink: 0, minWidth: 0 }}>
      <Typography className="summary-row-title" sx={{ fontSize: '0.8125rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {label}
      </Typography>
      {caption && (
        <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {caption}
        </Typography>
      )}
    </Box>
    <Box className="summary-row-track" sx={{ flex: 1, minWidth: 40, height: 10, bgcolor: 'action.hover', borderRadius: '999px', overflow: 'hidden' }}>
      <Box
        className="summary-row-fill"
        sx={{
          position: 'relative',
          height: '100%',
          width: `${clampPct(barPct)}%`,
          bgcolor: barColor,
          borderRadius: '999px',
          overflow: 'hidden',
        }}
      >
        {/* Light sweep that glides across the fill while the row is hovered. */}
        <Box
          className="summary-row-sheen"
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            width: '45%',
            background: 'linear-gradient(100deg, transparent 0%, rgba(255, 255, 255, 0.18) 50%, transparent 100%)',
            transform: 'translateX(-160%)',
            pointerEvents: 'none',
          }}
        />
      </Box>
    </Box>
    <Box sx={{ width: 58, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.25 }}>
      <Box className="summary-row-value" sx={{ display: 'inline-flex' }}>
        {trailing}
      </Box>
      {onClick && (
        <ChevronRightRounded className="summary-row-chevron" sx={{ fontSize: '0.95rem', color: 'primary.main', opacity: 0, flexShrink: 0, ml: '-2px' }} />
      )}
    </Box>
  </Box>
)
